import React, { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useTasks } from '@/hooks/useData';
import { useThemeStore } from '@/stores';
import { 
  RefreshCw, 
  ChevronDown, 
  X, 
  CheckSquare, 
  Play, 
  Clock, 
  Search, 
  ChevronRight, 
  ChevronLeft,
  ChevronsRight,
  ChevronsLeft,
  Filter
} from 'lucide-react';

const statusColors: Record<string, { bg: string; color: string; border: string }> = {
  pending: { bg: 'rgba(99,102,241,0.15)', color: '#818cf8', border: 'rgba(99,102,241,0.4)' },
  processing: { bg: 'rgba(59,130,246,0.15)', color: '#60a5fa', border: 'rgba(59,130,246,0.4)' },
  completed: { bg: 'rgba(16,185,129,0.15)', color: '#34d399', border: 'rgba(16,185,129,0.4)' },
  cancelled: { bg: 'rgba(239,68,68,0.15)', color: '#f87171', border: 'rgba(239,68,68,0.4)' },
};

const statusLabels: Record<string, string> = {
  pending: 'معلق',
  processing: 'قيد التنفيذ',
  completed: 'مكتمل',
  cancelled: 'ملغى',
};

const priorityColors: Record<string, { bg: string; color: string }> = {
  high: { bg: 'rgba(239,68,68,0.15)', color: '#f87171' },
  medium: { bg: 'rgba(245,158,11,0.15)', color: '#fbbf24' },
  low: { bg: 'rgba(16,185,129,0.15)', color: '#34d399' },
};

const priorityLabels: Record<string, string> = {
  high: 'عالية',
  medium: 'متوسطة',
  low: 'منخفضة',
};

const demoTasks = [
  { id: 'T0001', requestId: 'REQ-2024002', description: 'مهمة صيانة رقم 0001', assignee: 'أحمد الخفرجاني', priority: 'medium', status: 'processing', date: '2024-02-02' },
  { id: 'T0002', requestId: 'REQ-2024003', description: 'مهمة فحص الشبكات 0002', assignee: 'علي الشبكات', priority: 'low', status: 'completed', date: '2024-03-03' },
  { id: 'T0003', requestId: 'REQ-2024004', description: 'مهمة تكوين رقم 0003', assignee: 'كريم النقاي', priority: 'high', status: 'cancelled', date: '2024-04-04' },
  { id: 'T0004', requestId: 'REQ-2024005', description: 'مهمة تركيب رقم 0004', assignee: 'حسن الصيانة', priority: 'medium', status: 'pending', date: '2024-05-05' },
  { id: 'T0005', requestId: 'REQ-2024006', description: 'مهمة صيانة رقم 0005', assignee: 'محمد الفي', priority: 'low', status: 'processing', date: '2024-06-06' },
  { id: 'T0006', requestId: 'REQ-2024007', description: 'مهمة فحص رقم 0006', assignee: 'أحمد الخفرجاني', priority: 'high', status: 'completed', date: '2024-07-07' },
  { id: 'T0007', requestId: 'REQ-2024008', description: 'مهمة تكوين رقم 0007', assignee: 'علي الشبكات', priority: 'medium', status: 'cancelled', date: '2024-08-08' },
  { id: 'T0008', requestId: 'REQ-2024009', description: 'تركيب راوتر وتوصيل الفايبر', assignee: 'عمر التميمي', priority: 'high', status: 'pending', date: '2024-08-10' },
  { id: 'T0009', requestId: 'REQ-2024010', description: 'فحص إشارة الكابل الضوئي', assignee: 'حيدر الكرخي', priority: 'low', status: 'completed', date: '2024-08-12' },
  { id: 'T0010', requestId: 'REQ-2024011', description: 'إعادة تهيئة إعدادات PPPoE', assignee: 'زيد الكاظمي', priority: 'medium', status: 'processing', date: '2024-08-15' },
  { id: 'T0011', requestId: 'REQ-2024012', description: 'لحام شعيرة ضوئية في البوكس', assignee: 'أحمد الخفرجاني', priority: 'high', status: 'processing', date: '2024-08-18' },
  { id: 'T0012', requestId: 'REQ-2024013', description: 'استبدال جهاز ONT للمشترك', assignee: 'علي الشبكات', priority: 'medium', status: 'completed', date: '2024-08-20' },
];

const TasksPage: React.FC = () => {
  const { t } = useTranslation();
  const { theme } = useThemeStore();
  const isLight = theme === 'light';

  const { data: rawData, isLoading, isFetching, refetch } = useTasks();
  const [statusFilter, setStatusFilter] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [showStatusMenu, setShowStatusMenu] = useState(false);

  // ── Pagination State ──
  const [pageNumber, setPageNumber] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const tasks = useMemo(() => {
    const items = (rawData as any)?.items || (rawData as any)?.data;
    if (!Array.isArray(items) || items.length === 0) return demoTasks;
    return items.map((item: any, idx: number) => {
      const statusKey = item.status || 'Not started';
      let uiStatus = 'pending';
      if (statusKey === 'Completed' || statusKey === 'Done') {
        uiStatus = 'completed';
      } else if (statusKey === 'InProgress' || statusKey === 'Processing') {
        uiStatus = 'processing';
      } else if (statusKey === 'Canceled' || statusKey === 'Cancelled') {
        uiStatus = 'cancelled';
      }

      return {
        id: item.self?.id?.substring(0, 8) || item.id?.substring(0, 8) || `T${String(idx + 1).padStart(4, '0')}`,
        requestId: item.requestId?.substring(0, 8) || item.request?.id?.substring(0, 8) || 'REQ—',
        description: item.self?.displayValue || item.description || item.title || 'مهمة فنية',
        assignee: item.customer?.displayValue || item.assignee || item.customerName || '—',
        priority: item.priority || (idx % 3 === 0 ? 'high' : idx % 2 === 0 ? 'medium' : 'low'),
        status: uiStatus,
        date: item.createdAt ? new Date(item.createdAt).toLocaleDateString('ar-IQ') : '—',
      };
    });
  }, [rawData]);

  const filtered = useMemo(() => {
    return tasks.filter((task: any) => {
      const matchesStatus = !statusFilter || task.status === statusFilter;
      if (!matchesStatus) return false;

      if (searchQuery.trim()) {
        const query = searchQuery.trim().toLowerCase();
        const inId = String(task.id || '').toLowerCase().includes(query);
        const inReq = String(task.requestId || '').toLowerCase().includes(query);
        const inDesc = String(task.description || '').toLowerCase().includes(query);
        const inAssignee = String(task.assignee || '').toLowerCase().includes(query);
        return inId || inReq || inDesc || inAssignee;
      }
      return true;
    });
  }, [tasks, statusFilter, searchQuery]);

  // Reset page when filter or search changes
  React.useEffect(() => {
    setPageNumber(1);
  }, [statusFilter, searchQuery, pageSize]);

  // Total counts for KPI summary
  const counts = useMemo(() => ({
    cancelled: tasks.filter((t: any) => t.status === 'cancelled').length,
    completed: tasks.filter((t: any) => t.status === 'completed').length,
    processing: tasks.filter((t: any) => t.status === 'processing').length,
    pending: tasks.filter((t: any) => t.status === 'pending').length,
  }), [tasks]);

  // Pagination calculations
  const totalTasks = filtered.length;
  const totalPages = Math.max(1, Math.ceil(totalTasks / pageSize));
  const currentPage = Math.min(pageNumber, totalPages);
  const startRecord = totalTasks === 0 ? 0 : (currentPage - 1) * pageSize + 1;
  const endRecord = Math.min(currentPage * pageSize, totalTasks);

  const paginatedTasks = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return filtered.slice(start, start + pageSize);
  }, [filtered, currentPage, pageSize]);

  const summaryCards = [
    { label: 'ملغى', count: counts.cancelled, icon: X, iconColor: '#f87171', bg: isLight ? '#fef2f2' : 'rgba(239,68,68,0.1)', border: isLight ? '#fecaca' : 'rgba(239,68,68,0.2)' },
    { label: 'مكتمل', count: counts.completed, icon: CheckSquare, iconColor: '#34d399', bg: isLight ? '#ecfdf5' : 'rgba(16,185,129,0.1)', border: isLight ? '#a7f3d0' : 'rgba(16,185,129,0.2)' },
    { label: 'قيد التنفيذ', count: counts.processing, icon: Play, iconColor: '#60a5fa', bg: isLight ? '#eff6ff' : 'rgba(59,130,246,0.1)', border: isLight ? '#bfdbfe' : 'rgba(59,130,246,0.2)' },
    { label: 'معلق', count: counts.pending, icon: Clock, iconColor: '#818cf8', bg: isLight ? '#eef2ff' : 'rgba(99,102,241,0.1)', border: isLight ? '#c7d2fe' : 'rgba(99,102,241,0.2)' },
  ];

  // Theme style constants
  const bgCard = isLight ? '#ffffff' : 'rgba(13,31,60,0.7)';
  const borderCard = isLight ? '1px solid #e2e8f0' : '1px solid rgba(255,255,255,0.08)';
  const textPrimary = isLight ? '#0f172a' : '#e2e8f0';
  const textSecondary = isLight ? '#64748b' : '#94a3b8';
  const inputBg = isLight ? '#f8fafc' : 'rgba(255,255,255,0.06)';
  const inputBorder = isLight ? '#cbd5e1' : 'rgba(255,255,255,0.12)';

  return (
    <div style={{ padding: '20px', direction: 'rtl', minHeight: '100%', color: textPrimary }}>
      {/* Page header */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          marginBottom: '20px',
          flexWrap: 'wrap',
          gap: '12px'
        }}
      >
        <div style={{ textAlign: 'right' }}>
          <h1 style={{ color: textPrimary, fontSize: '22px', fontWeight: 700, marginBottom: '4px' }}>
            {t('tasks.title') || 'إدارة المهام'}
          </h1>
          <p style={{ color: textSecondary, fontSize: '13px' }}>
            {t('common.total') || 'الإجمالي'}: <strong style={{ color: '#3b82f6' }}>{totalTasks}</strong> مهمة {statusFilter || searchQuery ? '(مفلترة)' : ''}
          </p>
        </div>
        <button
          onClick={() => refetch()}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            background: isLight ? '#f1f5f9' : 'rgba(255,255,255,0.06)',
            border: `1px solid ${inputBorder}`,
            borderRadius: '8px',
            padding: '8px 14px',
            color: textSecondary,
            cursor: 'pointer',
            fontSize: '13px',
            fontWeight: 600,
            transition: 'all 0.2s',
          }}
        >
          <RefreshCw size={14} style={isFetching ? { animation: 'spin 1s linear infinite' } : {}} />
          {t('common.refresh') || 'تحديث'}
        </button>
      </div>

      {/* Summary cards */}
      <div className="tasks-stats-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(135px, 1fr))', gap: '12px', marginBottom: '20px' }}>
        {summaryCards.map((card) => {
          const Icon = card.icon;
          return (
            <div
              key={card.label}
              style={{
                background: card.bg,
                border: `1px solid ${card.border}`,
                borderRadius: '12px',
                padding: '16px 20px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                direction: 'rtl',
                boxShadow: isLight ? '0 1px 3px rgba(0,0,0,0.05)' : 'none',
              }}
            >
              <div style={{ textAlign: 'right' }}>
                <div style={{ color: textSecondary, fontSize: '13px', marginBottom: '4px', fontWeight: 600 }}>{card.label}</div>
                <div style={{ color: textPrimary, fontSize: '28px', fontWeight: 800 }}>{card.count}</div>
              </div>
              <div
                style={{
                  width: '40px',
                  height: '40px',
                  borderRadius: '10px',
                  background: isLight ? '#ffffff' : 'rgba(0,0,0,0.2)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  boxShadow: isLight ? '0 1px 4px rgba(0,0,0,0.08)' : 'none',
                }}
              >
                <Icon size={20} color={card.iconColor} />
              </div>
            </div>
          );
        })}
      </div>

      {/* Controls: Search + Filter dropdown */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', flexWrap: 'wrap', gap: '12px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flex: '1', maxWidth: '500px' }}>
          {/* Search Input */}
          <div style={{ position: 'relative', width: '100%' }}>
            <Search size={16} style={{ position: 'absolute', right: '12px', top: '50%', transform: 'translateY(-50%)', color: textSecondary }} />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="البحث برقم المهمة، الوصف، أو المسؤول..."
              style={{
                width: '100%',
                background: inputBg,
                border: `1px solid ${inputBorder}`,
                borderRadius: '8px',
                padding: '9px 36px 9px 12px',
                color: textPrimary,
                fontSize: '13px',
                outline: 'none',
                direction: 'rtl',
              }}
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                style={{
                  position: 'absolute',
                  left: '10px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  background: 'none',
                  border: 'none',
                  color: textSecondary,
                  cursor: 'pointer',
                  padding: 0,
                }}
              >
                <X size={14} />
              </button>
            )}
          </div>
        </div>

        {/* Status Filter Menu */}
        <div style={{ position: 'relative' }}>
          <button
            onClick={() => setShowStatusMenu(!showStatusMenu)}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              background: inputBg,
              border: `1px solid ${inputBorder}`,
              borderRadius: '8px',
              padding: '9px 14px',
              color: textPrimary,
              cursor: 'pointer',
              fontSize: '13px',
              minWidth: '180px',
              justifyContent: 'space-between',
              direction: 'rtl',
              fontWeight: 500,
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <Filter size={14} style={{ color: '#3b82f6' }} />
              <span>{statusFilter ? (statusLabels[statusFilter] || statusFilter) : 'جميع الحالات'}</span>
            </div>
            <ChevronDown size={14} style={{ color: textSecondary }} />
          </button>
          {showStatusMenu && (
            <div
              style={{
                position: 'absolute',
                top: '100%',
                right: 0,
                marginTop: '4px',
                background: isLight ? '#ffffff' : '#0d1f3c',
                border: `1px solid ${inputBorder}`,
                borderRadius: '8px',
                padding: '4px',
                zIndex: 100,
                minWidth: '180px',
                boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
              }}
            >
              {[{ value: '', label: 'جميع الحالات' }, ...Object.entries(statusLabels).map(([value, label]) => ({ value, label }))].map((s) => (
                <button
                  key={s.value}
                  onClick={() => { setStatusFilter(s.value); setShowStatusMenu(false); }}
                  style={{
                    width: '100%',
                    padding: '8px 12px',
                    textAlign: 'right',
                    background: statusFilter === s.value ? (isLight ? '#eff6ff' : 'rgba(59,130,246,0.15)') : 'transparent',
                    border: 'none',
                    color: statusFilter === s.value ? '#3b82f6' : textPrimary,
                    cursor: 'pointer',
                    fontSize: '13px',
                    fontWeight: statusFilter === s.value ? 700 : 400,
                    borderRadius: '6px',
                    direction: 'rtl',
                  }}
                >
                  {s.label}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Table */}
      <div
        style={{
          background: bgCard,
          border: borderCard,
          borderRadius: '12px',
          overflowX: 'auto',
          boxShadow: isLight ? '0 1px 4px rgba(0,0,0,0.05)' : 'none',
        }}
      >
        <table className="responsive-table" style={{ width: '100%', borderCollapse: 'collapse', direction: 'rtl' }}>
          <thead>
            <tr style={{ background: isLight ? '#f8fafc' : 'rgba(255,255,255,0.04)' }}>
              {['رقم المهمة', 'رقم الطلب', 'الوصف', 'المسؤول', 'الأولوية', 'الحالة', 'التاريخ'].map((col) => (
                <th
                  key={col}
                  style={{
                    padding: '12px 16px',
                    textAlign: 'right',
                    color: textSecondary,
                    fontSize: '12px',
                    fontWeight: 700,
                    borderBottom: borderCard,
                    whiteSpace: 'nowrap',
                  }}
                >
                  {col}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr>
                <td colSpan={7} style={{ padding: '40px', textAlign: 'center', color: textSecondary }}>
                  جاري التحميل...
                </td>
              </tr>
            ) : paginatedTasks.length === 0 ? (
              <tr>
                <td colSpan={7} style={{ padding: '40px', textAlign: 'center', color: textSecondary }}>
                  لا توجد مهام مطابقة للبحث أو التصفية الحالية.
                </td>
              </tr>
            ) : (
              paginatedTasks.map((task: any, i: number) => {
                const sc = statusColors[task.status] || statusColors.pending;
                const pc = priorityColors[task.priority] || priorityColors.medium;
                return (
                  <tr
                    key={task.id || i}
                    style={{ 
                      borderBottom: borderCard, 
                      transition: 'background 0.15s' 
                    }}
                    onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.background = isLight ? '#f8fafc' : 'rgba(255,255,255,0.03)'; }}
                    onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.background = 'transparent'; }}
                  >
                    <td data-label="رقم المهمة" style={{ padding: '12px 16px', color: '#3b82f6', fontSize: '13px', fontWeight: 600, whiteSpace: 'nowrap' }}>{task.id}</td>
                    <td data-label="رقم الطلب" style={{ padding: '12px 16px', color: textSecondary, fontSize: '13px', whiteSpace: 'nowrap' }}>{task.requestId}</td>
                    <td data-label="الوصف" style={{ padding: '12px 16px', color: textPrimary, fontSize: '13px' }}>{task.description}</td>
                    <td data-label="المسؤول" style={{ padding: '12px 16px', color: textPrimary, fontSize: '13px', fontWeight: 500 }}>{task.assignee}</td>
                    <td data-label="الأولوية" style={{ padding: '12px 16px' }}>
                      <span style={{ background: pc.bg, color: pc.color, padding: '3px 10px', borderRadius: '20px', fontSize: '12px', fontWeight: 600, whiteSpace: 'nowrap' }}>
                        {priorityLabels[task.priority] || task.priority}
                      </span>
                    </td>
                    <td data-label="الحالة" style={{ padding: '12px 16px' }}>
                      <span style={{ background: sc.bg, color: sc.color, border: `1px solid ${sc.border}`, padding: '3px 10px', borderRadius: '20px', fontSize: '12px', fontWeight: 600, whiteSpace: 'nowrap' }}>
                        {statusLabels[task.status] || task.status}
                      </span>
                    </td>
                    <td data-label="التاريخ" style={{ padding: '12px 16px', color: textSecondary, fontSize: '13px', direction: 'ltr', textAlign: 'right' }}>{task.date}</td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* ─── Pagination Controls ─── */}
      {totalTasks > 0 && (
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginTop: '16px',
            padding: '12px 16px',
            background: bgCard,
            border: borderCard,
            borderRadius: '10px',
            color: textSecondary,
            fontSize: '13px',
            flexWrap: 'wrap',
            gap: '12px',
            boxShadow: isLight ? '0 1px 3px rgba(0,0,0,0.05)' : 'none',
          }}
        >
          {/* Record Count & Page Size Selector */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '14px', flexWrap: 'wrap' }}>
            <div>
              عرض <strong style={{ color: textPrimary }}>{startRecord}</strong> - <strong style={{ color: textPrimary }}>{endRecord}</strong> من إجمالي <strong style={{ color: '#3b82f6' }}>{totalTasks}</strong> مهمة
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <span>لكل صفحة:</span>
              <select
                value={pageSize}
                onChange={(e) => setPageSize(Number(e.target.value))}
                style={{
                  background: inputBg,
                  border: `1px solid ${inputBorder}`,
                  borderRadius: '6px',
                  padding: '4px 8px',
                  color: textPrimary,
                  fontSize: '12px',
                  fontWeight: 600,
                  outline: 'none',
                  cursor: 'pointer',
                }}
              >
                <option value={5}>5</option>
                <option value={10}>10</option>
                <option value={25}>25</option>
                <option value={50}>50</option>
              </select>
            </div>
          </div>

          {/* Navigation Buttons */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            {/* First Page */}
            <button
              disabled={currentPage <= 1}
              onClick={() => setPageNumber(1)}
              title="الصفحة الأولى"
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '32px',
                height: '32px',
                background: currentPage <= 1 ? (isLight ? '#f1f5f9' : 'rgba(255,255,255,0.02)') : (isLight ? '#f8fafc' : 'rgba(255,255,255,0.06)'),
                border: `1px solid ${inputBorder}`,
                borderRadius: '6px',
                color: currentPage <= 1 ? (isLight ? '#cbd5e1' : '#475569') : textPrimary,
                cursor: currentPage <= 1 ? 'not-allowed' : 'pointer',
                transition: 'all 0.15s',
              }}
            >
              <ChevronsRight size={16} />
            </button>

            {/* Previous Page */}
            <button
              disabled={currentPage <= 1}
              onClick={() => setPageNumber((p) => Math.max(1, p - 1))}
              title="الصفحة السابقة"
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                padding: '5px 10px',
                height: '32px',
                background: currentPage <= 1 ? (isLight ? '#f1f5f9' : 'rgba(255,255,255,0.02)') : (isLight ? '#f8fafc' : 'rgba(255,255,255,0.06)'),
                border: `1px solid ${inputBorder}`,
                borderRadius: '6px',
                color: currentPage <= 1 ? (isLight ? '#cbd5e1' : '#475569') : textPrimary,
                cursor: currentPage <= 1 ? 'not-allowed' : 'pointer',
                fontSize: '12px',
                fontWeight: 600,
                transition: 'all 0.15s',
              }}
            >
              <ChevronRight size={15} />
              <span>السابق</span>
            </button>

            {/* Numbered Page Buttons */}
            {Array.from({ length: Math.min(5, totalPages) }, (_, idx) => {
              let pageNum: number;
              if (totalPages <= 5) {
                pageNum = idx + 1;
              } else if (currentPage <= 3) {
                pageNum = idx + 1;
              } else if (currentPage >= totalPages - 2) {
                pageNum = totalPages - 4 + idx;
              } else {
                pageNum = currentPage - 2 + idx;
              }

              const isActive = currentPage === pageNum;
              return (
                <button
                  key={pageNum}
                  onClick={() => setPageNumber(pageNum)}
                  style={{
                    width: '32px',
                    height: '32px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    background: isActive ? '#3b82f6' : (isLight ? '#f8fafc' : 'rgba(255,255,255,0.04)'),
                    border: isActive ? '1px solid #3b82f6' : `1px solid ${inputBorder}`,
                    borderRadius: '6px',
                    color: isActive ? '#ffffff' : textPrimary,
                    cursor: 'pointer',
                    fontSize: '12px',
                    fontWeight: isActive ? 700 : 500,
                    transition: 'all 0.15s',
                  }}
                >
                  {pageNum}
                </button>
              );
            })}

            {/* Next Page */}
            <button
              disabled={currentPage >= totalPages}
              onClick={() => setPageNumber((p) => Math.min(totalPages, p + 1))}
              title="الصفحة التالية"
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                padding: '5px 10px',
                height: '32px',
                background: currentPage >= totalPages ? (isLight ? '#f1f5f9' : 'rgba(255,255,255,0.02)') : (isLight ? '#f8fafc' : 'rgba(255,255,255,0.06)'),
                border: `1px solid ${inputBorder}`,
                borderRadius: '6px',
                color: currentPage >= totalPages ? (isLight ? '#cbd5e1' : '#475569') : textPrimary,
                cursor: currentPage >= totalPages ? 'not-allowed' : 'pointer',
                fontSize: '12px',
                fontWeight: 600,
                transition: 'all 0.15s',
              }}
            >
              <span>التالي</span>
              <ChevronLeft size={15} />
            </button>

            {/* Last Page */}
            <button
              disabled={currentPage >= totalPages}
              onClick={() => setPageNumber(totalPages)}
              title="الصفحة الأخيرة"
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '32px',
                height: '32px',
                background: currentPage >= totalPages ? (isLight ? '#f1f5f9' : 'rgba(255,255,255,0.02)') : (isLight ? '#f8fafc' : 'rgba(255,255,255,0.06)'),
                border: `1px solid ${inputBorder}`,
                borderRadius: '6px',
                color: currentPage >= totalPages ? (isLight ? '#cbd5e1' : '#475569') : textPrimary,
                cursor: currentPage >= totalPages ? 'not-allowed' : 'pointer',
                transition: 'all 0.15s',
              }}
            >
              <ChevronsLeft size={16} />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default TasksPage;